import React, { useState, useEffect } from 'react';
import { assetApi, campusApi } from '../services/api';
import { PageHead, Btn, TableWrap, Th, Td, FormGroup, Input, Select, Spinner, Toast, Modal, Empty, Card } from '../components/UI';

export default function AssetsPage() {
  const [assets,   setAssets]   = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState(null);
  const [modal,    setModal]    = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState({ campusID:'', assetName:'', totalQuantity:'', description:'' });

  useEffect(() => {
    campusApi.getAll()
      .then(r => setCampuses(r.data.data || []))
      .catch(() => setCampuses([{ campusID:1, campusName:'Chinchwad' },{ campusID:2, campusName:'Hinjawadi' }]));
  }, []);

  useEffect(() => { load(); }, [campusId]);

  async function load() {
    setLoading(true);
    try {
      const res = await assetApi.getAll(campusId || undefined, true);
      setAssets(res.data.data || []);
    } catch {
      setAssets(DEMO.filter(a => !campusId || a.campusID === parseInt(campusId)));
    } finally { setLoading(false); }
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, campusID: parseInt(form.campusID), totalQuantity: parseInt(form.totalQuantity) };
      if (modal === 'create') await assetApi.create(payload);
      else await assetApi.update(modal.assetID, { ...payload, isActive: modal.isActive });
      setToast({ msg: `Asset ${modal === 'create' ? 'created' : 'updated'} successfully.`, type:'success' });
      setModal(null); load();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Save failed.', type:'error' });
    } finally { setSaving(false); }
  }

  async function toggleActive(asset) {
    try {
      asset.isActive ? await assetApi.deactivate(asset.assetID) : await assetApi.activate(asset.assetID);
      setToast({ msg: `Asset ${asset.isActive ? 'deactivated' : 'activated'}.`, type:'success' });
      load();
    } catch { setToast({ msg:'Failed to update.', type:'error' }); }
  }

  function pct(used, total) { return total ? Math.round(used / total * 100) : 0; }

  return (
    <div className="page-anim">
      <PageHead
        title="Assets"
        subtitle="Equipment and resources management"
        actions={
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <select value={campusId} onChange={e => setCampusId(e.target.value)}
              style={{ padding:'7px 12px', fontSize:13, border:'1.5px solid var(--border)', borderRadius:8, fontFamily:'var(--font-body)', outline:'none', background:'white' }}>
              <option value="">All Campuses</option>
              {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
            </select>
            <Btn variant="primary" onClick={() => { setForm({ campusID: campuses[0]?.campusID || '', assetName:'', totalQuantity:'', description:'' }); setModal('create'); }}>+ Add Asset</Btn>
          </div>
        }
      />

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'3rem', gap:10 }}><Spinner size={24}/><span style={{ color:'var(--text2)' }}>Loading…</span></div>
      ) : assets.length === 0 ? <Empty icon="🪑" text="No assets found" /> : (
        <TableWrap>
          <thead>
            <tr>
              <Th>#</Th><Th>Asset</Th><Th>Campus</Th><Th>Total Qty</Th><Th>Utilisation</Th><Th>Available</Th><Th>Status</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a, i) => {
              const used = Math.floor(a.totalQuantity * 0.3); // demo usage
              const p    = pct(used, a.totalQuantity);
              const barColor = p > 80 ? 'var(--red)' : p > 50 ? 'var(--orange)' : 'var(--green)';
              return (
                <tr key={a.assetID}>
                  <Td style={{ color:'var(--text3)', fontWeight:600 }}>{String(i + 1).padStart(2,'0')}</Td>
                  <Td>
                    <div style={{ fontWeight:700 }}>{a.assetName}</div>
                    {a.description && <div style={{ fontSize:11, color:'var(--text3)' }}>{a.description}</div>}
                  </Td>
                  <Td>{a.campusName}</Td>
                  <Td style={{ textAlign:'center', fontWeight:700 }}>{a.totalQuantity}</Td>
                  <Td style={{ minWidth:140 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ flex:1, height:7, background:'var(--bg2)', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${p}%`, background:barColor, borderRadius:4, transition:'width .4s' }} />
                      </div>
                      <span style={{ fontSize:11, fontWeight:600, color:'var(--text2)', minWidth:36 }}>{used}/{a.totalQuantity}</span>
                    </div>
                  </Td>
                  <Td style={{ textAlign:'center' }}>
                    <span style={{ fontWeight:800, fontSize:16, color:'var(--green)' }}>{a.totalQuantity - used}</span>
                  </Td>
                  <Td>
                    <span style={{ background: a.isActive ? 'var(--green-light)' : 'var(--bg2)', color: a.isActive ? 'var(--green)' : 'var(--text3)', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20 }}>
                      {a.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ display:'flex', gap:6 }}>
                      <Btn size="sm" variant="ghost" onClick={() => { setForm({ campusID: a.campusID, assetName: a.assetName, totalQuantity: a.totalQuantity, description: a.description || '' }); setModal(a); }}>✏️</Btn>
                      <Btn size="sm" variant={a.isActive ? 'red' : 'green'} onClick={() => toggleActive(a)}>{a.isActive ? '🔒' : '✅'}</Btn>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      )}

      {modal && (
        <Modal title={modal === 'create' ? 'Add Asset' : 'Edit Asset'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem', marginBottom:'1rem' }}>
              <FormGroup label="Campus *">
                <Select value={form.campusID} onChange={e => setForm(f => ({ ...f, campusID: e.target.value }))} required>
                  <option value="">— Select Campus —</option>
                  {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="Asset Name *">
                <Input value={form.assetName} onChange={e => setForm(f => ({ ...f, assetName: e.target.value }))} placeholder="e.g. Chairs" required />
              </FormGroup>
              <FormGroup label="Total Quantity *">
                <Input type="number" min={0} value={form.totalQuantity} onChange={e => setForm(f => ({ ...f, totalQuantity: e.target.value }))} placeholder="e.g. 200" required />
              </FormGroup>
              <FormGroup label="Description">
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" />
              </FormGroup>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Btn type="submit" variant="primary" disabled={saving} style={{ flex:1, justifyContent:'center' }}>
                {saving ? <Spinner size={16} color="white" /> : modal === 'create' ? '✅ Create' : '💾 Save'}
              </Btn>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            </div>
          </form>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const DEMO = [
  { assetID:1, campusID:1, campusName:'Chinchwad', assetName:'Chairs',     totalQuantity:200, isActive:true  },
  { assetID:2, campusID:1, campusName:'Chinchwad', assetName:'Microphone', totalQuantity:4,   isActive:true  },
  { assetID:3, campusID:1, campusName:'Chinchwad', assetName:'TV Trolley', totalQuantity:2,   isActive:true  },
  { assetID:4, campusID:1, campusName:'Chinchwad', assetName:'Projector',  totalQuantity:3,   isActive:true  },
  { assetID:5, campusID:1, campusName:'Chinchwad', assetName:'Podium',     totalQuantity:2,   isActive:false },
  { assetID:6, campusID:2, campusName:'Hinjawadi', assetName:'Chairs',     totalQuantity:150, isActive:true  },
  { assetID:7, campusID:2, campusName:'Hinjawadi', assetName:'Microphone', totalQuantity:3,   isActive:true  },
  { assetID:8, campusID:2, campusName:'Hinjawadi', assetName:'Projector',  totalQuantity:2,   isActive:true  },
];
